import React, { useEffect, useRef, useState } from "react";
import { LANDING } from "@/constants/testIds";
import { Star } from "lucide-react";

const AVATARS = [
  "/assets/ugc_woman_1.png",
  "/assets/ugc_woman_2.png",
  "https://i.pravatar.cc/120?img=12",
  "https://i.pravatar.cc/120?img=68",
];

const HERO_VIDEOS = [
  "/assets/hero_atelier.mp4",
  "/assets/hero_atelier_2.mp4",
  "/assets/hero_atelier_3.mp4",
];

const HERO_CAPTIONS = [
  {
    label: "Hand-made · Dubai",
    headline: "14 hours of artisan craft per pillow.",
    sticker: "live atelier",
    stickerCls: "sticker-lime",
  },
  {
    label: "Master tailoring",
    headline: "Each seam hand-stitched. Every single one.",
    sticker: "hand-stitched",
    stickerCls: "sticker-lavender",
  },
  {
    label: "Hungarian goose down",
    headline: "800+ fill power. The world's softest cloud.",
    sticker: "goose down",
    stickerCls: "sticker-lime",
  },
];

export default function HeroV2() {
  const videoRef = useRef(null);
  const idxRef = useRef(0);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const playNext = () => {
      idxRef.current = (idxRef.current + 1) % HERO_VIDEOS.length;
      el.src = HERO_VIDEOS[idxRef.current];
      el.muted = true;
      el.load();
      el.play().catch(() => {});
      setActiveIdx(idxRef.current);
    };
    el.muted = true;
    el.addEventListener("ended", playNext);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      el.removeEventListener("ended", playNext);
    };
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="aurora-wrap pt-32 pb-12 md:pt-40 md:pb-20">
      <div className="container-x text-center f-up">
        <h1
          data-testid={LANDING.heroHeadline}
          className="text-[44px] sm:text-[64px] md:text-[84px] lg:text-[96px] leading-[0.95] font-black tracking-[-0.03em] text-[#0e0e10]"
        >
          The pillow you knew
          <br />
          <span className="relative inline-block">existed.</span>
        </h1>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <span className="sticker sticker-lime text-[13px] md:text-[15px] whitespace-nowrap px-5 py-2.5">
            🪶 100 nights to fall in love · or send it back
          </span>
        </div>

        {/* ABOVE-FOLD PRICE + SOCIAL PROOF */}
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
          <div className="inline-flex items-baseline gap-2 bg-white border border-black/10 rounded-full px-5 py-2.5 shadow-sm">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6b6b72]">From</span>
            <span className="text-[24px] font-black text-[#0e0e10] tabular-nums leading-none">$147</span>
            <span className="text-[12px] text-[#8a8a8a] line-through tabular-nums">$199</span>
            <span className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#22c55e]">−26%</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-white border border-black/10 rounded-full px-4 py-2.5 shadow-sm">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={13} className="fill-[#d8f06b] text-[#d8f06b]" />
              ))}
            </div>
            <span className="text-[13px] font-extrabold text-[#0e0e10] tabular-nums">4.9</span>
            <span className="text-[11px] text-[#6b6b72] font-semibold">
              · 2,847 verified
            </span>
          </div>
        </div>

        <p className="mt-7 text-[16px] md:text-[19px] text-[#5a5a60] max-w-2xl mx-auto leading-relaxed">
          If you've been looking — really looking — you already know.
          The good ones aren't sold in stores. They're hand-stitched in Dubai
          for the rooms where a night costs more than a month's rent.
          <br className="hidden md:block" />
          <strong className="text-[#0e0e10]">You finally found it.</strong>
        </p>

        {/* HORIZONTAL HERO VIDEO */}
        <div className="mt-10 mx-auto max-w-4xl">
          <div className="relative aspect-video rounded-[32px] overflow-hidden bg-[#0e0e10] shadow-2xl border border-black/5">
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              preload="auto"
              data-testid="hero-video"
              className="absolute inset-0 w-full h-full object-cover bg-[#0e0e10]"
            >
              <source src={HERO_VIDEOS[0]} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            <div className="absolute top-5 left-5 right-5 flex items-center justify-between pointer-events-none">
              <div className="flex items-center gap-1.5">
                {HERO_VIDEOS.map((_, i) => (
                  <span
                    key={i}
                    className={`h-1 rounded-full transition-all duration-500 ${
                      i === activeIdx ? "w-7 bg-white" : "w-3 bg-white/40"
                    }`}
                  />
                ))}
              </div>
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/70">
                {String(activeIdx + 1).padStart(2, "0")} / {String(HERO_VIDEOS.length).padStart(2, "0")}
              </span>
            </div>
            <div
              key={activeIdx}
              className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-white pointer-events-none fade-in-up"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">
                  {HERO_CAPTIONS[activeIdx].label}
                </div>
                <div className="font-extrabold text-[18px] md:text-[22px] leading-tight mt-1 max-w-[80%]">
                  {HERO_CAPTIONS[activeIdx].headline}
                </div>
              </div>
              <span
                className={`hidden md:inline-block sticker ${HERO_CAPTIONS[activeIdx].stickerCls} text-[11px]`}
              >
                {HERO_CAPTIONS[activeIdx].sticker}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-2 md:gap-3">
          <span className="section-pill">🪶 800+ fill power</span>
          <span className="section-pill">🌾 organic Egyptian cotton</span>
          <span className="section-pill">🇦🇪 hand-made in Dubai</span>
          <span className="section-pill">🌙 100-night trial</span>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <button
            data-testid={LANDING.heroCta}
            onClick={() => scrollTo("shop")}
            className="pill-btn px-8 py-4 text-[15px]"
          >
            See the pillow →
          </button>
          <button
            onClick={() => scrollTo("quiz")}
            className="pill-btn pill-btn-light px-7 py-4 text-[15px]"
          >
            Take the 30s quiz
          </button>
        </div>

        <div className="mt-4 text-[12px] text-[#6b6b72] inline-flex items-center gap-3">
          <span>✓ Free worldwide shipping</span>
          <span className="opacity-30">·</span>
          <span>✓ 100-night trial</span>
          <span className="opacity-30">·</span>
          <span>✓ Sleep on it first. Decide after.</span>
        </div>

        <div className="mt-7 inline-flex items-center gap-3 px-4 py-2 bg-white/70 backdrop-blur border border-black/5 rounded-full">
          <div className="flex -space-x-2">
            {AVATARS.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="w-7 h-7 rounded-full border-2 border-white object-cover"
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Star size={14} fill="#facc15" stroke="none" />
            <span className="text-[13px] font-semibold text-[#0e0e10]">
              4.9/5 from 12,000+ sleepers
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
